/**
 * FX Presets
 * 
 * Flat registry of effect presets for the FX system.
 * Each preset can combine ripple, shake, timeRamp, and particles.
 */

import type { TimeRampPreset } from './TimeRamps';

/**
 * Configuration for ripple effect within a preset
 */
export interface FXRippleConfig {
  /** Maximum distortion amplitude */
  amplitude: number;
  /** Duration in seconds */
  duration: number;
  /** Wave frequency - ring density */
  frequency?: number;
  /** Expansion speed */
  speed?: number;
  /** Time ramp preset */
  timeRamp?: TimeRampPreset;
}

/**
 * Configuration for camera shake within a preset
 */
export interface FXShakeConfig {
  /** Maximum offset amplitude */
  amplitude: number;
  /** Duration in seconds */
  duration: number;
  /** Shake frequency in Hz */
  frequency?: number;
  /** Time ramp preset */
  timeRamp?: TimeRampPreset;
}

/**
 * Configuration for time ramp (slow-motion) within a preset
 */
export interface FXTimeRampConfig {
  /** Target time scale (0.1 = 10% speed) */
  targetScale: number;
  /** Duration to reach target scale (seconds) */
  rampDuration: number;
  /** How long to hold at target scale (seconds) */
  holdDuration?: number;
  /** Easing preset for the ramp */
  easing?: TimeRampPreset;
}

/**
 * Complete FX preset configuration
 */
export interface FXPresetConfig {
  /** Preset name for identification */
  name: string;
  /** Screen-space ripple distortion */
  ripple?: FXRippleConfig;
  /** Camera shake effect */
  shake?: FXShakeConfig;
  /** Time ramp (slow-motion) effect */
  timeRamp?: FXTimeRampConfig;
  /** Particle preset name (from ParticleManager) */
  particles?: string;
}

/**
 * FX configuration for use in AttackDef.
 * Can be created from presets and overridden with custom values.
 * All fields optional - only triggers effects that are defined.
 */
export interface FXConfig {
  /** Screen-space ripple distortion */
  ripple?: FXRippleConfig;
  /** Camera shake effect */
  shake?: FXShakeConfig;
  /** Time ramp (slow-motion) effect */
  timeRamp?: FXTimeRampConfig;
  /** Particle preset name (from ParticleManager) */
  particles?: string;
  /** Intensity multiplier for all effects (default: 1.0) */
  intensity?: number;
}

/**
 * Available preset names
 */
export type FXPresetName = 
  | 'melee_hit'
  | 'projectile_hit'
  | 'heavy_hit'
  | 'magic_hit'
  | 'ice_impact'
  | 'explosion'
  | 'ground_pound';

/**
 * Preset registry
 */
const FX_PRESETS: Record<FXPresetName, FXPresetConfig> = {
  /**
   * Standard melee attack hit
   * Quick, punchy feedback
   */
  melee_hit: {
    name: 'melee_hit',
    ripple: {
      amplitude: 0.015,
      duration: 0.25,
      frequency: 12,
      speed: 2.0,
      timeRamp: 'explosion',
    },
    shake: {
      amplitude: 0.08,
      duration: 0.15,
      frequency: 35,
      timeRamp: 'punch',
    },
    timeRamp: {
      targetScale: 0.7,
      rampDuration: 0.02,
      holdDuration: 0.03,
      easing: 'punch',
    },
  },

  /**
   * Standard projectile impact
   * Medium intensity, slightly longer
   */
  projectile_hit: {
    name: 'projectile_hit',
    ripple: {
      amplitude: 0.02,
      duration: 0.35,
      frequency: 10,
      speed: 1.8,
      timeRamp: 'explosion',
    },
    shake: {
      amplitude: 0.1,
      duration: 0.2,
      frequency: 30,
      timeRamp: 'easeOut',
    },
    timeRamp: {
      targetScale: 0.5,
      rampDuration: 0.02,
      holdDuration: 0.05,
      easing: 'punch',
    },
  },

  /**
   * Heavy attack or thrown weapon impact
   * Strong feedback with longer time ramp
   */
  heavy_hit: {
    name: 'heavy_hit',
    ripple: {
      amplitude: 0.035,
      duration: 0.5,
      frequency: 8,
      speed: 1.5,
      timeRamp: 'explosion',
    },
    shake: {
      amplitude: 0.18,
      duration: 0.35,
      frequency: 25,
      timeRamp: 'punch',
    },
    timeRamp: {
      targetScale: 0.3,
      rampDuration: 0.03,
      holdDuration: 0.1,
      easing: 'punch',
    },
    particles: 'hit_flash',
  },

  /**
   * Magic attack impact (ice bolt, magic missile)
   * Ethereal feel with higher frequency ripple
   */
  magic_hit: {
    name: 'magic_hit',
    ripple: {
      amplitude: 0.025,
      duration: 0.4,
      frequency: 15,
      speed: 2.2,
      timeRamp: 'explosion',
    },
    shake: {
      amplitude: 0.12,
      duration: 0.25,
      frequency: 40,
      timeRamp: 'easeOut',
    },
    timeRamp: {
      targetScale: 0.4,
      rampDuration: 0.02,
      holdDuration: 0.06,
      easing: 'punch',
    },
    particles: 'magic_burst',
  },

  /**
   * Ice projectile impact
   * Frosty, crystalline feel with ice particles
   */
  ice_impact: {
    name: 'ice_impact',
    ripple: {
      amplitude: 0.022,
      duration: 0.35,
      frequency: 18,
      speed: 2.5,
      timeRamp: 'explosion',
    },
    shake: {
      amplitude: 0.1,
      duration: 0.2,
      frequency: 45,
      timeRamp: 'easeOut',
    },
    timeRamp: {
      targetScale: 0.5,
      rampDuration: 0.015,
      holdDuration: 0.04,
      easing: 'punch',
    },
    particles: 'ice_burst',
  },

  /**
   * Explosion effect
   * Maximum intensity, big ripple and shake
   */
  explosion: {
    name: 'explosion',
    ripple: {
      amplitude: 0.05,
      duration: 0.6,
      frequency: 6,
      speed: 1.2,
      timeRamp: 'explosion',
    },
    shake: {
      amplitude: 0.25,
      duration: 0.5,
      frequency: 20,
      timeRamp: 'punch',
    },
    timeRamp: {
      targetScale: 0.2,
      rampDuration: 0.03,
      holdDuration: 0.15,
      easing: 'punch',
    },
    particles: 'fire_explosion',
  },

  /**
   * Ground pound / stomp attack
   * Strong vertical emphasis
   */
  ground_pound: {
    name: 'ground_pound',
    ripple: {
      amplitude: 0.04,
      duration: 0.45,
      frequency: 5,
      speed: 1.0,
      timeRamp: 'explosion',
    },
    shake: {
      amplitude: 0.2,
      duration: 0.4,
      frequency: 15,
      timeRamp: 'punch',
    },
    timeRamp: {
      targetScale: 0.25,
      rampDuration: 0.02,
      holdDuration: 0.12,
      easing: 'punch',
    },
    particles: 'dust_puff',
  },
};

/**
 * Get an FX preset by name
 * @throws Error if preset not found
 */
export function getFXPreset(name: FXPresetName): FXPresetConfig {
  const preset = FX_PRESETS[name];
  if (!preset) {
    throw new Error(`FX preset not found: ${name}`);
  }
  return preset;
}

/**
 * Get all available preset names
 */
export function getFXPresetNames(): FXPresetName[] {
  return Object.keys(FX_PRESETS) as FXPresetName[];
}

/**
 * Check if a preset exists
 */
export function hasFXPreset(name: string): name is FXPresetName {
  return name in FX_PRESETS;
}

/**
 * Create an FXConfig from a preset.
 * Use spread operator to override specific values:
 * 
 * @example
 * // Use preset as-is
 * impactFX: fromFXPreset('heavy_hit')
 * 
 * @example
 * // Override ripple amplitude
 * impactFX: {
 *   ...fromFXPreset('heavy_hit'),
 *   ripple: { ...fromFXPreset('heavy_hit').ripple!, amplitude: 0.08 }
 * }
 * 
 * @example
 * // Remove time ramp from preset
 * impactFX: {
 *   ...fromFXPreset('melee_hit'),
 *   timeRamp: undefined
 * }
 */
export function fromFXPreset(name: FXPresetName): FXConfig {
  const preset = getFXPreset(name);
  return {
    ripple: preset.ripple ? { ...preset.ripple } : undefined,
    shake: preset.shake ? { ...preset.shake } : undefined,
    timeRamp: preset.timeRamp ? { ...preset.timeRamp } : undefined,
    particles: preset.particles,
  };
}
